import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconInstallments from '@/assets/icons/heroIcons/Installments.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconSearch from '@/assets/icons/heroIcons/Search.svg';
import IconCursor from '@/assets/icons/heroIcons/Cursor-click.svg';
import IconPayment from '@/assets/icons/heroIcons/Deferred-billing-payment.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { Calculator } from '@/sections/installments/Calculator';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { useClientOnly } from '@/hooks/useClientOnly';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(132);
  usePageView("loan");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["loan", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const registrationLink = useProductRegistrationLink("pujcka");
  const {
    isClient
  } = useClientOnly();
  let t1;
  if ($[1] !== registrationLink || $[2] !== t) {
    t1 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" /></Trans>
        } : {
          footer: <Button analyticsParam="loanRegistration" testId="loanRegistration" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={registrationLink}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/loan/cards/desktop/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/loan/cards/mobile/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = registrationLink;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cards = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("seo.title");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("seo.description");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== pageBanner?.title || $[12] !== t) {
    t5 = pageBanner?.title ?? t("banner.title");
    $[11] = pageBanner?.title;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== pageBanner?.text || $[15] !== t) {
    t6 = pageBanner?.text ?? t("banner.text");
    $[14] = pageBanner?.text;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== isClient) {
    t7 = isClient ? <div id={getSectionAnchorId("calculator")} className="scroll-mt-24"><Calculator compact={true} type="loan" /></div> : <div className="min-h-[44rem] w-full md:min-h-[45rem] xl:min-h-[43rem]" />;
    $[17] = isClient;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <Hero title={t5} text={t6} buttons={null} image={null} variant="mockupMobile" className="bg-blue-50 lg:pt-40!" contentClassName="xl:w-[35rem] xl:translate-x-10 xl:justify-center 4xl:w-[38rem] 4xl:translate-x-16" layoutClassName="xl:gap-8 4xl:gap-16" mediaClassName="mb-20 xl:mb-0 xl:max-w-[38.5rem] 4xl:mt-0 4xl:max-w-[43rem]" customImageContent={t7} />;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getSectionAnchorId("advantages");
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t) {
    t10 = t("advantages.title");
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <SectionHeadline className="mx-auto mb-10 max-w-192 px-0 md:mb-16" anchorKey="advantages.title">{t10}</SectionHeadline>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("advantages.items.1.title");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" />;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = <Trans t={t} i18nKey="advantages.items.1.text">{t13}</Trans>;
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IconCursor />;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t12 || $[35] !== t14) {
    t16 = {
      title: t12,
      paragraph: t14,
      icon: t15
    };
    $[34] = t12;
    $[35] = t14;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("advantages.items.2.title");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("advantages.items.2.text");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <IconSearch />;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t17 || $[43] !== t18) {
    t20 = {
      title: t17,
      paragraph: t18,
      icon: t19
    };
    $[42] = t17;
    $[43] = t18;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t("advantages.items.3.title");
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t("advantages.items.3.text");
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <IconPayment />;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t22) {
    t24 = {
      title: t21,
      paragraph: t22,
      icon: t23
    };
    $[50] = t21;
    $[51] = t22;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t16 || $[54] !== t20 || $[55] !== t24) {
    t25 = <InfoBoxes items={[t16, t20, t24]} variant="basic" />;
    $[53] = t16;
    $[54] = t20;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Link to={`${LINKS().packages}#mini`} testId="loanPackages" analyticsParam="loanPackages" />;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Link to={LINKS().revolving} testId="loanRevolving" analyticsParam="loanRevolving" />;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t) {
    t28 = <Text variant="p4" className="text-center text-gray-800"><Trans t={t} i18nKey="advantages.text">{t26}{t27}</Trans></Text>;
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t) {
    t29 = t("advantages.cta");
    $[61] = t;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== registrationLink || $[64] !== t29) {
    t30 = <Button to={registrationLink} testId="loanAdvantagesRegistration" analyticsParam="loanAdvantagesRegistration" size="lg" variant="primary" align="center">{t29}</Button>;
    $[63] = registrationLink;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t25 || $[67] !== t28 || $[68] !== t30) {
    t31 = <div className="flex flex-col gap-10 px-4 md:px-0 lg:gap-12">{t25}{t28}{t30}</div>;
    $[66] = t25;
    $[67] = t28;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t11 || $[71] !== t31) {
    t32 = <div className="relative z-10">{t11}{t31}</div>;
    $[70] = t11;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="bgCircle absolute top-12 right-0 h-[11.25rem] w-[11.25rem] translate-x-1/2 lg:top-0 lg:left-0 lg:h-125 lg:w-125 lg:-translate-x-1/4" />;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t32) {
    t34 = <AnimatedSection bgColor="bg-blue-100" id={t9}><AnimatedContent className="overflow-x-hidden">{t32}{t33}</AnimatedContent></AnimatedSection>;
    $[74] = t32;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = getSectionAnchorId("howItWorks.title");
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t) {
    t36 = t("howItWorks.title");
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t36) {
    t37 = <div className="mx-auto max-w-192"><SectionHeadline className="mb-10 md:mb-16" anchorKey="howItWorks.title">{t36}</SectionHeadline></div>;
    $[79] = t36;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== cards) {
    t38 = <StackCards imagePosition="left" cards={cards} />;
    $[81] = cards;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== t37 || $[84] !== t38) {
    t39 = <AnimatedContent>{t37}{t38}</AnimatedContent>;
    $[83] = t37;
    $[84] = t38;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = LINKS();
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Link to="/caste-dotazy/dalsi-caste-otazky" analyticsParam="faqLoan" testId="faqLoan" />;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== t) {
    t42 = <AnimatedContent className="pt-20 pb-10 lg:pt-40 lg:pb-20"><FaqIntro link={t40.faq} text={<Trans i18nKey="faq.text" t={t}>{t41}</Trans>} /></AnimatedContent>;
    $[88] = t;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <IconInstallments className="fillButtonIcon" />;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] !== t) {
    t44 = t("core:products.installments");
    $[91] = t;
    $[92] = t44;
  } else {
    t44 = $[92];
  }
  let t45;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = LINKS();
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t44) {
    t46 = {
      analyticsParam: "loanMoreInfoDeferredPayment",
      icon: t43,
      testId: "loanMoreInfoDeferredPayment",
      title: t44,
      to: t45.installments
    };
    $[94] = t44;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <IconMaxi className="fillButtonIcon" />;
    $[96] = t47;
  } else {
    t47 = $[96];
  }
  let t48;
  if ($[97] !== t) {
    t48 = t("core:products.revolving");
    $[97] = t;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  let t49;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = LINKS();
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  if ($[100] !== t48) {
    t50 = {
      analyticsParam: "loanMoreInfoRevolvingApplication",
      icon: t47,
      testId: "loanMoreInfoRevolvingApplication",
      title: t48,
      to: t49.revolving
    };
    $[100] = t48;
    $[101] = t50;
  } else {
    t50 = $[101];
  }
  let t51;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <IconThird className="fillButtonIcon" />;
    $[102] = t51;
  } else {
    t51 = $[102];
  }
  let t52;
  if ($[103] !== t) {
    t52 = t("core:products.payInThree");
    $[103] = t;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = LINKS();
    $[105] = t53;
  } else {
    t53 = $[105];
  }
  let t54;
  if ($[106] !== t52) {
    t54 = {
      analyticsParam: "loanMoreInfoPayInThree",
      icon: t51,
      testId: "loanMoreInfoPayInThree",
      title: t52,
      to: t53.payInThree
    };
    $[106] = t52;
    $[107] = t54;
  } else {
    t54 = $[107];
  }
  let t55;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <IconCard className="fillButtonIcon" />;
    $[108] = t55;
  } else {
    t55 = $[108];
  }
  let t56;
  if ($[109] !== t) {
    t56 = t("core:products.card");
    $[109] = t;
    $[110] = t56;
  } else {
    t56 = $[110];
  }
  let t57;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = LINKS();
    $[111] = t57;
  } else {
    t57 = $[111];
  }
  let t58;
  if ($[112] !== t56) {
    t58 = {
      analyticsParam: "loanMoreInfoCard",
      icon: t55,
      testId: "loanMoreInfoCard",
      title: t56,
      to: t57.card
    };
    $[112] = t56;
    $[113] = t58;
  } else {
    t58 = $[113];
  }
  let t59;
  if ($[114] !== t46 || $[115] !== t50 || $[116] !== t54 || $[117] !== t58) {
    t59 = <AnimatedContent className="relative z-10"><MoreInfoSection buttons={[t46, t50, t54, t58]} /></AnimatedContent>;
    $[114] = t46;
    $[115] = t50;
    $[116] = t54;
    $[117] = t58;
    $[118] = t59;
  } else {
    t59 = $[118];
  }
  let t60;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[119] = t60;
  } else {
    t60 = $[119];
  }
  let t61;
  if ($[120] !== t42 || $[121] !== t59) {
    t61 = <div className="relative overflow-hidden">{t42}{t59}{t60}</div>;
    $[120] = t42;
    $[121] = t59;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] !== t39 || $[124] !== t61) {
    t62 = <AnimatedSection bgColor="bg-blue-100" className="pb-0!" id={t35}>{t39}{t61}</AnimatedSection>;
    $[123] = t39;
    $[124] = t61;
    $[125] = t62;
  } else {
    t62 = $[125];
  }
  let t63;
  if ($[126] !== menuPromoBanners || $[127] !== t34 || $[128] !== t4 || $[129] !== t62 || $[130] !== t8) {
    t63 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t8}{t34}{t62}</Layout>;
    $[126] = menuPromoBanners;
    $[127] = t34;
    $[128] = t4;
    $[129] = t62;
    $[130] = t8;
    $[131] = t63;
  } else {
    t63 = $[131];
  }
  return t63;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('pujcka');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('loan'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'loan']))
    }
  };
}) satisfies GetStaticProps<Props>;